import React, { Fragment, useEffect, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, InformationCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { clsx } from 'clsx';
import { usePositionMarks } from '@/hooks';
import { TradeItem } from '@/types';
import { formatMarkPrice, priceDelta } from '@/utils/trades/mark-price-format';
import { formatDateTimeUtc } from '@/utils/format';

const { Panel: DialogPanel, Title: DialogTitle } = Dialog;
const { Child: TransitionChild } = Transition;

// Matches the leave duration on the panel TransitionChild below. Keeps the
// drawer's contents painted while it slides out instead of going blank the
// instant `trade` is cleared.
const LEAVE_TRANSITION_MS = 250;

// LR-478: the single-trade page peeks mark history in-place and passes its
// PositionDetail, which is NOT a TradeItem. Type the prop against the structural
// subset of fields this drawer actually reads (id, symbol_id, quantity,
// currency) so both a blotter TradeItem row and a PositionDetail satisfy it
// without a cast — mirrors PartialRecallModal's PartialRecallTarget pattern.
type MtmDrawerTarget = Pick<TradeItem, 'id' | 'symbol_id' | 'quantity' | 'currency'>;

interface MtmDetailsDrawerProps {
  trade: MtmDrawerTarget | null;
  onClose: () => void;
}

// Mark timestamps read in canonical UTC (`YYYY-MM-DD HH:MM UTC`, the desk standard
// used by Created At / "Price as of") instead of the viewer's local clock with a
// month name (LR-667). Unparseable input falls back to the raw string.
function formatTimestamp(iso: string): string {
  return formatDateTimeUtc(iso) || iso;
}

/**
 * Right-side drawer showing the full MarkToMarket history for one position.
 * Triggered by clicking a row on the MTM page. Read-only.
 *
 * "Repricing - MtM.xlsx" Scenario 1: "There should be a details tab which shows
 * the individual mark amount $5K for this trade with date applied."
 */
export const MtmDetailsDrawer: React.FC<MtmDetailsDrawerProps> = ({ trade, onClose }) => {
  // `trade` flips to null the instant the user clicks Close, but the panel
  // keeps animating out for ~250ms. Mirror it in local state so the header and
  // mark list stay painted through the leave transition instead of blanking.
  const [displayTrade, setDisplayTrade] = useState<MtmDrawerTarget | null>(trade);
  useEffect(() => {
    if (trade) {
      setDisplayTrade(trade);
      return;
    }
    const timer = setTimeout(() => setDisplayTrade(null), LEAVE_TRANSITION_MS);
    return () => clearTimeout(timer);
  }, [trade]);

  const positionId = displayTrade?.id ?? null;
  const { data: marks, isLoading, error } = usePositionMarks(positionId, displayTrade != null);

  return (
    <Transition show={trade != null} as={Fragment}>
      <Dialog as="div" className="relative z-[100]" onClose={onClose}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/20" aria-hidden="true" />
        </TransitionChild>

        <div className="fixed inset-y-0 right-0 flex max-w-full">
          <TransitionChild
            as={Fragment}
            enter="transform transition ease-out duration-250"
            enterFrom="translate-x-full"
            enterTo="translate-x-0"
            leave="transform transition ease-in duration-200"
            leaveFrom="translate-x-0"
            leaveTo="translate-x-full"
          >
            <DialogPanel className="w-screen max-w-md bg-white shadow-2xl flex flex-col h-full">
              <div className="flex items-start justify-between border-b border-gray-200 px-5 py-4">
                <div>
                  <DialogTitle as="h2" className="text-base font-semibold text-gray-900">
                    Mark history
                  </DialogTitle>
                  {displayTrade && (
                    <p className="mt-0.5 text-xs text-gray-500">
                      <span className="font-mono">{displayTrade.symbol_id}</span>
                      <span className="mx-1.5 text-gray-300">·</span>
                      <span>#{displayTrade.id}</span>
                      <span className="mx-1.5 text-gray-300">·</span>
                      <span>
                        {displayTrade.quantity?.toLocaleString('en-US')} {displayTrade.currency}
                      </span>
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                  aria-label="Close mark history"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto">
                {isLoading && (
                  <div className="p-5 space-y-3" aria-hidden="true">
                    {Array.from({ length: 4 }).map((_, i) => (
                      <div key={i} className="animate-pulse rounded border border-gray-100 p-3">
                        <div className="h-3 w-24 rounded bg-gray-200" />
                        <div className="mt-2 h-3 w-40 rounded bg-gray-200" />
                      </div>
                    ))}
                  </div>
                )}

                {error && (
                  <div className="m-5 flex items-start gap-2 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                    <ExclamationTriangleIcon className="h-5 w-5 shrink-0 text-red-400" />
                    <div>
                      <p className="font-medium">Failed to load mark history</p>
                      <p className="mt-0.5 text-xs text-red-600">
                        {error instanceof Error ? error.message : 'Unknown error'}
                      </p>
                    </div>
                  </div>
                )}

                {!isLoading && !error && marks && marks.length === 0 && (
                  <div className="flex flex-col items-center gap-2 py-16 px-5 text-center">
                    <InformationCircleIcon className="h-8 w-8 text-gray-300" />
                    <p className="text-sm font-medium text-gray-600">No marks yet</p>
                    <p className="text-xs text-gray-400">
                      This trade has not been marked to market.
                    </p>
                  </div>
                )}

                {!isLoading && !error && marks && marks.length > 0 && (
                  <ol className="divide-y divide-gray-100">
                    {marks.map((mark, idx) => {
                      const delta = priceDelta(marks, idx);
                      const isManual = mark.source === 'manual';
                      return (
                        <li key={mark.id} className="px-5 py-3">
                          <div className="flex items-baseline justify-between gap-2">
                            <span className="font-mono tabular-nums text-sm text-gray-900">
                              {formatMarkPrice(mark.price)}
                              <span className="ml-1 text-xs text-gray-500">{displayTrade?.currency}</span>
                            </span>
                            {delta !== null && delta !== 0 && (
                              <span
                                className={clsx(
                                  'font-mono tabular-nums text-xs',
                                  delta > 0 ? 'text-green-600' : 'text-red-600',
                                )}
                                title="Δ vs previous mark"
                              >
                                {delta > 0 ? '+' : '-'}
                                {Math.abs(delta).toLocaleString('en-US', {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 4,
                                })}
                              </span>
                            )}
                          </div>
                          <div className="mt-1 flex items-center gap-2 text-xs text-gray-500">
                            <time dateTime={mark.marked_at}>{formatTimestamp(mark.marked_at)}</time>
                            <span
                              className={clsx(
                                'inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide',
                                isManual
                                  ? 'bg-blue-50 text-blue-700'
                                  : 'bg-gray-100 text-gray-600',
                              )}
                            >
                              {isManual ? 'Manual' : 'Auto'}
                            </span>
                            {mark.marked_by && (
                              <span className="truncate" title={mark.marked_by}>
                                {mark.marked_by}
                              </span>
                            )}
                          </div>
                          {mark.notes && (
                            <p className="mt-1 text-xs italic text-gray-500">{mark.notes}</p>
                          )}
                        </li>
                      );
                    })}
                  </ol>
                )}
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  );
};
